import { cn } from '@/lib/utils';
import {
  PALIER_DESCRIPTION_MAX_CHARS,
  type ResolvedPalier,
  palierPosition,
} from '@mss_creator/shared';
import { Button, Icon, TextArea, TextField } from '@pms-digital/design-system';
import { useId, useState } from 'react';

/**
 * The 8 fixed paliers: a row of chips, plus an edit panel for whichever chip is
 * open (issue #1).
 *
 * The chip row stays the overview — compact, all 8 visible at once, which is
 * the only way to read a progression as a progression. What it cannot hold is a
 * description, so a description does not go in it: clicking a chip opens one
 * panel underneath the row, and closing it returns to the eight. Eight stacked
 * label+textarea forms would have said the same thing while making the shape of
 * the scale impossible to see.
 *
 * One panel at a time, deliberately: with eight open the row would scroll off
 * screen and the thing being edited would lose the context it is edited
 * against.
 *
 * Every palier is shown by its position among the 8 columns, 1 to 8 (issue #28),
 * never by its internal number: the model reads the same position, so a comment
 * that says « palier 3 » means the same thing to both.
 *
 * The component serves two levels without being duplicated (the company sheet
 * today, the matrix's Cadrage tab later). What differs between them is entirely
 * in `inherited` — the values one rung down, which decide what "overridden"
 * means and what "reset" restores — and in the wording of the reset button.
 */

export interface PaliersEditorProps {
  /** The effective values, as edited. The parent owns this draft and its saving. */
  paliers: ResolvedPalier[];
  /**
   * What each palier would resolve to if this level's override were cleared:
   * the global defaults on the company sheet, the company-resolved table on a
   * matrix. A palier whose draft differs from this is an override, and that is
   * the only thing the dot means.
   */
  inherited: ResolvedPalier[];
  /** e.g. "Rétablir la valeur par défaut" at company level, "Reprendre la valeur de l'entreprise" at matrix level. */
  resetLabel: string;
  /** Wording of the legend beside the row, naming what the dot marks at this level. */
  overriddenLegend: string;
  disabled?: boolean;
  /** Every palier can still be opened and read, but none can be changed (an archived company, issue #22). */
  readOnly?: boolean;
  onChange: (paliers: ResolvedPalier[]) => void;
}

function sameText(a: string | null, b: string | null): boolean {
  return (a ?? '') === (b ?? '');
}

export function PaliersEditor({
  paliers,
  inherited,
  resetLabel,
  overriddenLegend,
  disabled = false,
  readOnly = false,
  onChange,
}: PaliersEditorProps) {
  const [openNumber, setOpenNumber] = useState<ResolvedPalier['number'] | null>(null);
  const labelId = useId();
  const descriptionId = useId();

  const inheritedByNumber = new Map(inherited.map((palier) => [palier.number, palier]));

  function update(number: ResolvedPalier['number'], patch: Partial<ResolvedPalier>) {
    onChange(paliers.map((p) => (p.number === number ? { ...p, ...patch } : p)));
  }

  function isOverridden(palier: ResolvedPalier): boolean {
    const base = inheritedByNumber.get(palier.number);
    if (!base) return false;
    return !sameText(palier.label, base.label) || !sameText(palier.description, base.description);
  }

  const open = openNumber === null ? null : (paliers.find((p) => p.number === openNumber) ?? null);
  const openPosition = open ? palierPosition(open.number) : null;
  const openInherited = open ? (inheritedByNumber.get(open.number) ?? null) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div data-testid="paliers-chip-row" className="flex flex-1 flex-wrap gap-2">
          {paliers.map((palier) => {
            const overridden = isOverridden(palier);
            const isOpen = openNumber === palier.number;
            const position = palierPosition(palier.number);
            return (
              <button
                key={palier.number}
                type="button"
                disabled={disabled}
                aria-expanded={isOpen}
                aria-label={`Palier ${position} · ${palier.label}${overridden ? ' (personnalisé)' : ''}`}
                onClick={() => setOpenNumber(isOpen ? null : palier.number)}
                className={cn(
                  'flex items-center gap-1 rounded-full border-2 px-3 py-1 transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40',
                  isOpen
                    ? 'border-bleu-nuit bg-bleu-nuit-tint'
                    : 'border-gray-200 bg-white hover:border-gray-300',
                )}
              >
                <span
                  className={cn(
                    'font-semibold text-caption',
                    isOpen ? 'text-bleu-nuit' : 'text-gray-500',
                  )}
                >
                  {position} ·
                </span>
                <span className={cn('text-small', isOpen && 'font-semibold text-bleu-nuit')}>
                  {palier.label}
                </span>
                {overridden && (
                  <span
                    data-testid={`palier-override-dot-${palier.number}`}
                    aria-hidden="true"
                    className="size-[7px] shrink-0 rounded-full bg-bleu-nuit"
                  />
                )}
              </button>
            );
          })}
        </div>
        <span className="flex shrink-0 items-center gap-1 text-caption text-gray-600">
          <span aria-hidden="true" className="size-[7px] rounded-full bg-bleu-nuit" />
          {overriddenLegend}
        </span>
      </div>

      {open && openInherited && (
        <div
          data-testid={`palier-panel-${open.number}`}
          className="rounded-md border-2 border-bleu-nuit bg-white p-4"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-bleu-nuit text-small">Palier {openPosition}</span>
            {/* The inherited label, shown rather than merely restorable: without
                it "reset" is a button whose outcome you cannot predict. */}
            <span className="rounded-md bg-bleu-nuit-tint px-2 py-0 font-semibold text-bleu-nuit text-caption">
              Hérité : {openInherited.label}
            </span>
            <div className="flex-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled || readOnly || !isOverridden(open)}
              onClick={() =>
                update(open.number, {
                  label: openInherited.label,
                  description: openInherited.description,
                })
              }
            >
              <Icon name="undo" size={16} />
              {resetLabel}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Fermer le palier ${openPosition}`}
              onClick={() => setOpenNumber(null)}
            >
              <Icon name="close" size={16} />
            </Button>
          </div>

          <div className="mt-4 space-y-4">
            <div>
              <TextField
                label="Libellé"
                id={labelId}
                maxLength={200}
                value={open.label}
                disabled={disabled}
                readOnly={readOnly}
                // An empty label would leave a column with no header at all, and
                // the inherited value is a better answer than a blank one.
                onChange={(event) =>
                  update(open.number, { label: event.target.value || openInherited.label })
                }
              />
              <p className="mt-1 text-caption text-gray-600">
                200 caractères max. Un palier BRAAVO peut commencer par son code (« R6.6 - … », «
                A1.1 - … ») : le modèle s'en sert pour le situer.
              </p>
            </div>

            <div>
              <TextArea
                label={
                  <>
                    Description <span>· facultatif</span>
                  </>
                }
                id={descriptionId}
                maxLength={PALIER_DESCRIPTION_MAX_CHARS}
                value={open.description ?? ''}
                disabled={disabled}
                readOnly={readOnly}
                placeholder="Ce que ce palier signifie concrètement pour cette entreprise."
                // Empty and absent are the same thing here — both mean "the
                // model sees only the label" — so the field normalises to null
                // rather than persisting an empty string that would read as a
                // deliberate blank description.
                onChange={(event) =>
                  update(open.number, { description: event.target.value || null })
                }
              />
              <p className="mt-1 text-caption text-gray-600">
                {PALIER_DESCRIPTION_MAX_CHARS} caractères max. Fournie au modèle avec le libellé,
                sous «&nbsp;Palier {openPosition} sur 8&nbsp;».
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
